import { type DefaultOptions, MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api";
import { isReportableApiError, reportError } from "@/telemetry/telemetry";

// Every failed query/mutation passes through here: report server faults and
// network errors once, centrally, so features never need their own reporting.
const report = (source: string) => (error: unknown) => {
  if (isReportableApiError(error)) reportError(error, { source });
};

/** Retries for a rate-limited (429) read, each after the backend's Retry-After. */
export const MAX_RATE_LIMIT_RETRIES = 3;
const MAX_RETRY_DELAY_MS = 30_000;

const isRateLimited = (error: unknown): error is ApiError => error instanceof ApiError && error.status === 429;

/**
 * How long to wait before retry `failureCount` (from 0): a 429's Retry-After (plus a little jitter, so several
 * tabs don't come back at once), otherwise TanStack's default backoff. Both capped at 30s.
 */
export function retryDelay(failureCount: number, error: unknown, random: () => number = Math.random): number {
  if (isRateLimited(error) && error.retryAfterSeconds !== null) {
    return Math.min(MAX_RETRY_DELAY_MS, error.retryAfterSeconds * 1000 * (1 + 0.25 * random()));
  }
  return Math.min(MAX_RETRY_DELAY_MS, 1000 * 2 ** failureCount);
}

export function createQueryClient(overrides: DefaultOptions["queries"] = {}): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({ onError: report("query") }),
    mutationCache: new MutationCache({ onError: report("mutation") }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        // Don't refetch an ERRORED query just because a new component mounted
        // and subscribed to it. With no cached data, that refetch resets the
        // query to `pending`, so a layout that gates on isPending (AppLayout's
        // profile gate) unmounts the children, the fetch fails again, the
        // children remount... an infinite request loop on any backend 500.
        retryOnMount: false,
        // Don't retry client errors (401/403/404/422...); they won't fix themselves. A 429 does: the
        // backend's rate limit says when (Retry-After), so wait that long and try again a few times.
        retry: (failureCount, error) => {
          if (isRateLimited(error)) return failureCount < MAX_RATE_LIMIT_RETRIES;
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return failureCount < 2;
        },
        retryDelay,
        ...overrides,
      },
    },
  });
}
